import type { AxiosInstance } from 'axios';
import { ISessionManagerExternalProvider } from 'interfaces/providers/ISessionManagerExternalProvider';
import { IApiArchiveSessionModel } from 'interfaces/session/IApiArchiveSessionModel';
import { ISessionMode, ISessionModeRequest } from 'interfaces/session/ISessionMode';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { injectable } from 'inversify';
import { Result } from 'services/Result';
import { ContextStore } from 'stores/ContextStore';

@injectable()
export class SessionManagerServiceExternal implements ISessionManagerExternalProvider {
  private axiosInstance: AxiosInstance;

  private contextStore: ContextStore;

  constructor(axiosInstance: AxiosInstance, contextStore: ContextStore) {
    this.axiosInstance = axiosInstance;
    this.contextStore = contextStore;
  }

  async getCurrentSessionMode() {
    const response = await this.axiosInstance.get<Result<ISessionMode>>('sessionmode');
    if (!response.data.isError && response.data.data) {
      return response.data.data as ISessionMode;
    }
    throw new Error('Failed to fetch session mode');
  }

  async getSessionMetadata(sessionId: string) {
    const response = await this.axiosInstance.get<Result<IApiSessionMetadataUpdated>>(`metadata/${sessionId}`);
    if (!response.data.isError && response.data.data) {
      return response.data.data as IApiSessionMetadataUpdated;
    }
    throw new Error('Failed to fetch session mode');
  }

  async getArchiveRegularSessions(skip: number, take: number) {
    const response = await this.axiosInstance.get<Result<IApiArchiveSessionModel[]>>(`metadata/regular/skip/${skip}/take/${take}`);
    if (!response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to fetch sessions');
  }

  async getArchiveBpSessions(skip: number, take: number) {
    const response = await this.axiosInstance.get<Result<IApiArchiveSessionModel[]>>(`metadata/bp/skip/${skip}/take/${take}`);
    if (!response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to fetch batting practices');
  }

  async changeSessionMode(sessionMode: ISessionMode, isOperatorlessTaggingEnabledForSelectedSession: boolean) {
    if (this.contextStore.activeField) {
      sessionMode.systemId = this.contextStore.activeField.systemId;
    }

    const payload: ISessionModeRequest = {
      sessionMode: sessionMode.sessionMode,
      sessionType: sessionMode.sessionType,
      sessionId: sessionMode.sessionId,
      scheduleReference: sessionMode.gameScheduleReference,
      strikeZone: sessionMode.strikeZone,
      privateSession: sessionMode.privateSession,
      isVideoCaptureEnabled: sessionMode.isVideoCaptureEnabled,
      isPlayerTrackingEnabled: sessionMode.isPlayerTrackingEnabled,
      systemId: sessionMode.systemId,
      issuer: 'TaggingApp',
      isOperatorlessTaggingEnabled: isOperatorlessTaggingEnabledForSelectedSession,
    };

    const response = await this.axiosInstance.post<Result<ISessionMode>>('sessionmanagement/changeMode', payload);
    if (!response.data.isError && response.data.data) {
      return response.data.data as ISessionMode;
    }
    throw new Error('Failed to change session mode');
  }
}
